import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Film,
  Star,
  Clock,
  Calendar,
  Type,
  Clapperboard,
  Users,
  Tv,
  ChevronDown,
  Sparkles,
  Loader2,
  X,
  RotateCcw,
} from 'lucide-react';
import type { Filters, MediaType } from '../types/movie';
import { GENRES, UNIFIED_GENRES, TV_ONLY_GENRES, LANGUAGES, CURRENT_YEAR, DEFAULT_FILTERS } from '../utils/constants';
import { RangeSlider, PresetButton } from './RangeSlider';
import { searchPeople } from '../api/tmdb';
import { debounce, getProfileUrl } from '../utils/helpers';

type FilterTab = 'genre' | 'ourRating' | 'imdb' | 'rt' | 'runtime' | 'year' | 'language' | 'director' | 'actor' | 'stream';

interface FilterBarProps {
  filters: Filters;
  onChange: (filters: Filters) => void;
  mediaType?: MediaType;
}

interface PersonSuggestion {
  id: number;
  name: string;
  profile_path: string | null;
  known_for_department: string;
  popularity?: number;
}

const getTabConfig = (isTV: boolean): { id: FilterTab; label: string; icon: typeof Film }[] => [
  { id: 'genre', label: 'Genre', icon: Film },
  { id: 'ourRating', label: 'Our Rating', icon: Sparkles },
  { id: 'imdb', label: 'IMDb', icon: Star },
  { id: 'rt', label: 'Rotten Tomatoes', icon: Star },
  { id: 'runtime', label: isTV ? 'Episode Runtime' : 'Runtime', icon: Clock },
  { id: 'year', label: isTV ? 'First Air Date' : 'Year', icon: Calendar },
  { id: 'language', label: 'Language', icon: Type },
  { id: 'director', label: isTV ? 'Creators' : 'Directors', icon: Clapperboard },
  { id: 'actor', label: 'Actors', icon: Users },
  { id: 'stream', label: 'Stream', icon: Tv },
];

export function FilterBar({ filters, onChange, mediaType = 'movie' }: FilterBarProps) {
  const isTV = mediaType === 'tv';
  const TABS = getTabConfig(isTV);
  // Use unified genres - same list for both Movies and TV, plus TV-only genres when in TV mode
  const genreList = isTV ? [...UNIFIED_GENRES, ...TV_ONLY_GENRES] : UNIFIED_GENRES;
  const [activeTab, setActiveTab] = useState<FilterTab | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Local state for actor/director search inputs
  const [actorInput, setActorInput] = useState('');
  const [directorInput, setDirectorInput] = useState('');

  // Autocomplete suggestions
  const [actorSuggestions, setActorSuggestions] = useState<PersonSuggestion[]>([]);
  const [directorSuggestions, setDirectorSuggestions] = useState<PersonSuggestion[]>([]);
  const [loadingActors, setLoadingActors] = useState(false);
  const [loadingDirectors, setLoadingDirectors] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveTab(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search for actors - sorted by popularity
  const searchActors = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) {
        setActorSuggestions([]);
        return;
      }
      setLoadingActors(true);
      try {
        const results = await searchPeople(query);
        // Filter to show mostly actors and sort by popularity
        const actors = results
          .filter(p => p.known_for_department === 'Acting')
          .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
          .slice(0, 8);
        // If no actors found, show all results sorted by popularity
        setActorSuggestions(actors.length > 0 ? actors : results.sort((a, b) => (b.popularity || 0) - (a.popularity || 0)).slice(0, 8));
      } catch (err) {
        console.error('Error searching actors:', err);
      } finally {
        setLoadingActors(false);
      }
    }, 300),
    []
  );

  // Debounced search for directors - sorted by popularity
  const searchDirectors = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) {
        setDirectorSuggestions([]);
        return;
      }
      setLoadingDirectors(true);
      try {
        const results = await searchPeople(query);
        // Filter to show mostly directors and sort by popularity
        const directors = results
          .filter(p => p.known_for_department === 'Directing')
          .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
          .slice(0, 8);
        // If no directors found, show all results sorted by popularity
        setDirectorSuggestions(directors.length > 0 ? directors : results.sort((a, b) => (b.popularity || 0) - (a.popularity || 0)).slice(0, 8));
      } catch (err) {
        console.error('Error searching directors:', err);
      } finally {
        setLoadingDirectors(false);
      }
    }, 300),
    []
  );

  // Handle actor input change
  const handleActorInputChange = (value: string) => {
    setActorInput(value);
    searchActors(value);
  };

  // Handle director input change
  const handleDirectorInputChange = (value: string) => {
    setDirectorInput(value);
    searchDirectors(value);
  };

  // Select actor from suggestions
  const selectActor = (person: PersonSuggestion) => {
    onChange({ ...filters, actorSearch: person.name });
    setActorInput('');
    setActorSuggestions([]);
  };

  // Select director from suggestions
  const selectDirector = (person: PersonSuggestion) => {
    onChange({ ...filters, directorSearch: person.name });
    setDirectorInput('');
    setDirectorSuggestions([]);
  };

  const getFilterCount = (tab: FilterTab): number => {
    switch (tab) {
      case 'genre':
        return filters.genres.length;
      case 'ourRating':
        return (filters.ourRatingRange[0] > 0 || filters.ourRatingRange[1] < 10) ? 1 : 0;
      case 'imdb':
        return (filters.ratingRange[0] > 0 || filters.ratingRange[1] < 10) ? 1 : 0;
      case 'rt':
        return (filters.rtRatingRange[0] > 0 || filters.rtRatingRange[1] < 100) ? 1 : 0;
      case 'runtime':
        return (filters.runtimeRange[0] > 0 || filters.runtimeRange[1] < 300) ? 1 : 0;
      case 'year':
        return (filters.yearRange[0] > 1920 || filters.yearRange[1] < CURRENT_YEAR) ? 1 : 0;
      case 'language':
        return filters.languages.length;
      case 'director':
        return filters.directorSearch ? 1 : 0;
      case 'actor':
        return filters.actorSearch ? 1 : 0;
      case 'stream':
        return 0;
      default:
        return 0;
    }
  };

  const handleTabClick = (tab: FilterTab) => {
    setActiveTab(activeTab === tab ? null : tab);
    // Clear suggestions when switching tabs
    setActorSuggestions([]);
    setDirectorSuggestions([]);
    setActorInput('');
    setDirectorInput('');
  };

  const updateFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    onChange({ ...filters, [key]: value });
  };

  const toggleGenre = (genreId: number) => {
    const newGenres = filters.genres.includes(genreId)
      ? filters.genres.filter(id => id !== genreId)
      : [...filters.genres, genreId];
    updateFilter('genres', newGenres);
  };

  const toggleLanguage = (langCode: string) => {
    const newLanguages = filters.languages.includes(langCode)
      ? filters.languages.filter(code => code !== langCode)
      : [...filters.languages, langCode];
    updateFilter('languages', newLanguages);
  };

  const renderDropdownContent = () => {
    switch (activeTab) {
      case 'genre':
        return (
          <div className="p-4">
            <div className="flex flex-wrap gap-2">
              {genreList.map((genre) => {
                const isSelected = filters.genres.includes(genre.id);
                return (
                  <PresetButton
                    key={genre.id}
                    label={genre.name}
                    isActive={isSelected}
                    onClick={() => toggleGenre(genre.id)}
                    color="cyan"
                  />
                );
              })}
            </div>
          </div>
        );

      case 'ourRating':
        return (
          <div className="p-5 space-y-5">
            <div className="flex items-center gap-2 text-slate-300">
              <Sparkles size={18} className="text-purple-400" />
              <span className="text-sm">
                Our Rating combines IMDb (60%) and Rotten Tomatoes (40%) scores
              </span>
            </div>
            <RangeSlider
              min={0}
              max={10}
              step={0.5}
              value={filters.ourRatingRange}
              onChange={(value) => updateFilter('ourRatingRange', value)}
              label="Our Rating (0-10)"
              formatValue={(v) => v.toFixed(1)}
              gradient="from-purple-500 to-violet-500"
            />
            <div className="flex flex-wrap gap-2">
              {[7, 7.5, 8, 8.5, 9].map((rating) => (
                <PresetButton
                  key={rating}
                  label={`${rating}+`}
                  isActive={filters.ourRatingRange[0] === rating && filters.ourRatingRange[1] === 10}
                  onClick={() => updateFilter('ourRatingRange', [rating, 10])}
                  color="purple"
                />
              ))}
            </div>
          </div>
        );

      case 'imdb':
        return (
          <div className="p-5 space-y-5">
            <RangeSlider
              min={0}
              max={10}
              step={0.5}
              value={filters.ratingRange}
              onChange={(value) => updateFilter('ratingRange', value)}
              label="IMDb Rating (0-10)"
              formatValue={(v) => v.toFixed(1)}
              gradient="from-yellow-500 to-amber-500"
            />
            <div className="flex flex-wrap gap-2">
              {[7, 7.5, 8, 8.5, 9].map((rating) => (
                <PresetButton
                  key={rating}
                  label={`${rating}+`}
                  isActive={filters.ratingRange[0] === rating && filters.ratingRange[1] === 10}
                  onClick={() => updateFilter('ratingRange', [rating, 10])}
                  color="yellow"
                />
              ))}
            </div>
          </div>
        );

      case 'rt':
        return (
          <div className="p-5 space-y-5">
            <RangeSlider
              min={0}
              max={100}
              step={5}
              value={filters.rtRatingRange}
              onChange={(value) => updateFilter('rtRatingRange', value)}
              label="Rotten Tomatoes (0-100%)"
              formatValue={(v) => `${v}%`}
              gradient="from-red-500 to-rose-500"
            />
            <div className="flex flex-wrap gap-2">
              {[60, 70, 80, 90].map((rating) => (
                <PresetButton
                  key={rating}
                  label={`${rating}%+`}
                  isActive={filters.rtRatingRange[0] === rating && filters.rtRatingRange[1] === 100}
                  onClick={() => updateFilter('rtRatingRange', [rating, 100])}
                  color="red"
                />
              ))}
            </div>
          </div>
        );

      case 'runtime':
        return (
          <div className="p-5 space-y-5">
            <RangeSlider
              min={0}
              max={300}
              step={10}
              value={filters.runtimeRange}
              onChange={(value) => updateFilter('runtimeRange', value)}
              label="Runtime"
              formatValue={(v) => `${v}m`}
              gradient="from-green-500 to-emerald-500"
            />
            <div className="flex flex-wrap gap-2">
              {[
                { label: 'Short (<90m)', range: [0, 90] as [number, number] },
                { label: 'Medium (90-120m)', range: [90, 120] as [number, number] },
                { label: 'Long (120-180m)', range: [120, 180] as [number, number] },
                { label: 'Epic (180m+)', range: [180, 300] as [number, number] },
              ].map((preset) => (
                <PresetButton
                  key={preset.label}
                  label={preset.label}
                  isActive={filters.runtimeRange[0] === preset.range[0] && filters.runtimeRange[1] === preset.range[1]}
                  onClick={() => updateFilter('runtimeRange', preset.range)}
                  color="green"
                />
              ))}
            </div>
          </div>
        );

      case 'year':
        return (
          <div className="p-5 space-y-5">
            <RangeSlider
              min={1920}
              max={CURRENT_YEAR}
              step={1}
              value={filters.yearRange}
              onChange={(value) => updateFilter('yearRange', value)}
              label={isTV ? "First Air Date Year" : "Release Year"}
              formatValue={(v) => v.toString()}
              gradient="from-cyan-500 to-teal-500"
            />
            <div className="flex flex-wrap gap-2">
              {[
                { label: '2025', range: [2025, 2025] as [number, number] },
                { label: '2024', range: [2024, 2024] as [number, number] },
                { label: '2020s', range: [2020, 2029] as [number, number] },
                { label: '2010s', range: [2010, 2019] as [number, number] },
                { label: '2000s', range: [2000, 2009] as [number, number] },
                { label: '90s', range: [1990, 1999] as [number, number] },
                { label: '80s', range: [1980, 1989] as [number, number] },
                { label: 'Classic', range: [1920, 1979] as [number, number] },
              ].map((preset) => (
                <PresetButton
                  key={preset.label}
                  label={preset.label}
                  isActive={filters.yearRange[0] === preset.range[0] && filters.yearRange[1] === preset.range[1]}
                  onClick={() => updateFilter('yearRange', preset.range)}
                  color="cyan"
                />
              ))}
            </div>
          </div>
        );

      case 'language':
        return (
          <div className="p-4">
            <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto">
              {Object.entries(LANGUAGES).map(([code, name]) => {
                const isSelected = filters.languages.includes(code);
                return (
                  <PresetButton
                    key={code}
                    label={name}
                    isActive={isSelected}
                    onClick={() => toggleLanguage(code)}
                    color="cyan"
                  />
                );
              })}
            </div>
          </div>
        );

      case 'director':
        return (
          <div className="p-4">
            <div className="relative">
              <input
                type="text"
                value={directorInput}
                onChange={(e) => handleDirectorInputChange(e.target.value)}
                placeholder="Type to search directors..."
                className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition-all"
              />
              {loadingDirectors && (
                <Loader2 size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-cyan-400 animate-spin" />
              )}
            </div>

            {/* Suggestions dropdown */}
            {directorSuggestions.length > 0 && (
              <div className="mt-2 bg-slate-700 border border-slate-600 rounded-xl overflow-hidden">
                {directorSuggestions.map((person) => (
                  <button
                    key={person.id}
                    onClick={() => selectDirector(person)}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-600 transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-slate-800 overflow-hidden flex-shrink-0">
                      {person.profile_path ? (
                        <img
                          src={getProfileUrl(person.profile_path, 'small')}
                          alt={person.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-500">👤</div>
                      )}
                    </div>
                    <div>
                      <div className="text-white font-medium">{person.name}</div>
                      <div className="text-xs text-slate-400">{person.known_for_department}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {filters.directorSearch && (
              <p className="mt-3 text-sm text-slate-400">
                Filtering by: <span className="text-cyan-400 font-medium">{filters.directorSearch}</span>
              </p>
            )}
          </div>
        );

      case 'actor':
        return (
          <div className="p-4">
            <div className="relative">
              <input
                type="text"
                value={actorInput}
                onChange={(e) => handleActorInputChange(e.target.value)}
                placeholder="Type to search actors..."
                className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition-all"
              />
              {loadingActors && (
                <Loader2 size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-cyan-400 animate-spin" />
              )}
            </div>

            {/* Suggestions dropdown */}
            {actorSuggestions.length > 0 && (
              <div className="mt-2 bg-slate-700 border border-slate-600 rounded-xl overflow-hidden">
                {actorSuggestions.map((person) => (
                  <button
                    key={person.id}
                    onClick={() => selectActor(person)}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-600 transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-slate-800 overflow-hidden flex-shrink-0">
                      {person.profile_path ? (
                        <img
                          src={getProfileUrl(person.profile_path, 'small')}
                          alt={person.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-500">👤</div>
                      )}
                    </div>
                    <div>
                      <div className="text-white font-medium">{person.name}</div>
                      <div className="text-xs text-slate-400">{person.known_for_department}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {filters.actorSearch && (
              <p className="mt-3 text-sm text-slate-400">
                Filtering by: <span className="text-cyan-400 font-medium">{filters.actorSearch}</span>
              </p>
            )}
          </div>
        );

      case 'stream':
        return (
          <div className="p-6 text-slate-400 text-center">
            <div className="text-4xl mb-2">🎬</div>
            <p>Streaming availability coming soon...</p>
          </div>
        );

      default:
        return null;
    }
  };

  // Build active filter chips
  const getActiveFilterChips = () => {
    const chips: { label: string; onRemove: () => void; color: string }[] = [];

    // Genre chips
    filters.genres.forEach((genreId) => {
      const genre = [...UNIFIED_GENRES, ...TV_ONLY_GENRES].find(g => g.id === genreId);
      chips.push({
        label: genre?.name || GENRES[genreId] || `Genre ${genreId}`,
        onRemove: () => onChange({ ...filters, genres: filters.genres.filter(id => id !== genreId) }),
        color: 'cyan',
      });
    });

    // Year range chip
    if (filters.yearRange[0] > 1920 || filters.yearRange[1] < CURRENT_YEAR) {
      chips.push({
        label: filters.yearRange[0] === filters.yearRange[1]
          ? `Year: ${filters.yearRange[0]}`
          : `Year: ${filters.yearRange[0]} - ${filters.yearRange[1]}`,
        onRemove: () => onChange({ ...filters, yearRange: [1920, CURRENT_YEAR] }),
        color: 'cyan',
      });
    }

    // Runtime range chip
    if (filters.runtimeRange[0] > 0 || filters.runtimeRange[1] < 300) {
      chips.push({
        label: `Runtime: ${filters.runtimeRange[0]}m - ${filters.runtimeRange[1]}m`,
        onRemove: () => onChange({ ...filters, runtimeRange: [0, 300] }),
        color: 'green',
      });
    }

    // Our Rating range chip
    if (filters.ourRatingRange[0] > 0 || filters.ourRatingRange[1] < 10) {
      const minChanged = filters.ourRatingRange[0] > 0;
      const maxChanged = filters.ourRatingRange[1] < 10;
      let label = 'Our Rating: ';
      if (minChanged && maxChanged) {
        label += `${filters.ourRatingRange[0]} - ${filters.ourRatingRange[1]}`;
      } else if (minChanged) {
        label += `${filters.ourRatingRange[0]}+`;
      } else {
        label += `Up to ${filters.ourRatingRange[1]}`;
      }
      chips.push({
        label,
        onRemove: () => onChange({ ...filters, ourRatingRange: [0, 10] }),
        color: 'purple',
      });
    }

    // IMDB Rating range chip
    if (filters.ratingRange[0] > 0 || filters.ratingRange[1] < 10) {
      chips.push({
        label: `IMDb: ${filters.ratingRange[0]}+`,
        onRemove: () => onChange({ ...filters, ratingRange: [0, 10] }),
        color: 'yellow',
      });
    }

    // RT Rating range chip
    if (filters.rtRatingRange[0] > 0 || filters.rtRatingRange[1] < 100) {
      chips.push({
        label: `Rotten Tomatoes: ${filters.rtRatingRange[0]}%+`,
        onRemove: () => onChange({ ...filters, rtRatingRange: [0, 100] }),
        color: 'red',
      });
    }

    // Language chips
    filters.languages.forEach((langCode) => {
      chips.push({
        label: LANGUAGES[langCode] || langCode,
        onRemove: () => onChange({ ...filters, languages: filters.languages.filter(code => code !== langCode) }),
        color: 'cyan',
      });
    });

    // Actor search chip
    if (filters.actorSearch) {
      chips.push({
        label: `Actor: ${filters.actorSearch}`,
        onRemove: () => onChange({ ...filters, actorSearch: '' }),
        color: 'cyan',
      });
    }

    // Director search chip
    if (filters.directorSearch) {
      chips.push({
        label: `Director: ${filters.directorSearch}`,
        onRemove: () => onChange({ ...filters, directorSearch: '' }),
        color: 'cyan',
      });
    }

    return chips;
  };

  const activeChips = getActiveFilterChips();
  const hasActiveFilters = activeChips.length > 0;

  const resetAllFilters = () => {
    onChange(DEFAULT_FILTERS as Filters);
    setActiveTab(null);
  };

  const getChipColorClasses = (color: string) => {
    const colorMap: Record<string, string> = {
      cyan: 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300',
      purple: 'bg-purple-500/20 border-purple-500/50 text-purple-300',
      yellow: 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300',
      red: 'bg-red-500/20 border-red-500/50 text-red-300',
      green: 'bg-green-500/20 border-green-500/50 text-green-300',
    };
    return colorMap[color] || colorMap.cyan;
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Filter Tabs */}
      <div className="bg-gradient-to-r from-teal-600 via-yellow-500 to-orange-500">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center justify-center overflow-x-auto scrollbar-hide">
            {TABS.map((tab) => {
              const count = getFilterCount(tab.id);
              const isActive = activeTab === tab.id;
              const Icon = tab.icon;

              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all whitespace-nowrap ${
                    isActive
                      ? 'bg-white/20 text-white'
                      : 'text-white/90 hover:bg-white/10'
                  }`}
                >
                  <Icon size={16} />
                  <span>{tab.label}</span>
                  {count > 0 && (
                    <span className="bg-white text-teal-600 text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                      {count}
                    </span>
                  )}
                  <ChevronDown
                    size={14}
                    className={`transition-transform ${isActive ? 'rotate-180' : ''}`}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Active Filters - Always visible */}
      {hasActiveFilters && (
        <div className="bg-slate-800/80 border-b border-slate-700">
          <div className="max-w-screen-2xl mx-auto px-4 py-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-slate-400 text-sm font-medium">Active filters:</span>
              {activeChips.map((chip, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-1.5 px-2.5 py-1 border rounded-full text-xs transition-colors ${getChipColorClasses(chip.color)}`}
                >
                  <span>{chip.label}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      chip.onRemove();
                    }}
                    className="hover:bg-white/10 rounded-full p-0.5 transition-colors"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              <button
                onClick={resetAllFilters}
                className="flex items-center gap-1 px-2.5 py-1 text-slate-400 hover:text-white text-xs transition-colors"
              >
                <RotateCcw size={12} />
                <span>Clear all</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dropdown Panel */}
      {activeTab && (
        <div className="absolute top-full left-0 right-0 bg-slate-800/95 backdrop-blur-xl border-b border-slate-700 shadow-2xl z-50">
          <div className="max-w-screen-2xl mx-auto">
            {renderDropdownContent()}
          </div>
        </div>
      )}
    </div>
  );
}
